import React, { ReactNode } from 'react';
import { Button } from './Button';

export interface AlertProps {
  /**
   * Type/severity of the alert
   */
  type?: 'success' | 'error' | 'warning' | 'info';

  /**
   * Alert message content
   */
  children: ReactNode;

  /**
   * Optional close handler
   */
  onClose?: () => void;

  /**
   * Additional className
   */
  className?: string;
}

/**
 * Standardized Alert Component
 *
 * A reusable alert/banner component for displaying notifications,
 * errors, success messages, and warnings.
 *
 * @example
 * ```tsx
 * <Alert type="success">
 *   Account created successfully!
 * </Alert>
 *
 * <Alert type="error" onClose={() => setError(null)}>
 *   Invalid username or password
 * </Alert>
 *
 * <Alert type="warning">
 *   Your session will expire in 5 minutes
 * </Alert>
 * ```
 */
export const Alert: React.FC<AlertProps> = ({
  type = 'info',
  children,
  onClose,
  className = '',
}) => {
  const typeStyles = {
    success: 'bg-green-100 text-green-700 border-green-200',
    error: 'bg-red-100 text-red-700 border-red-200',
    warning: 'bg-orange-100 text-orange-700 border-orange-200',
    info: 'bg-blue-100 text-blue-700 border-blue-200',
  };

  const iconMap = {
    success: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    error: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    warning: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    info: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  };

  const baseClasses = 'p-4 rounded-lg border flex items-center gap-3';
  const combinedClassName = `${baseClasses} ${typeStyles[type]} ${className}`.trim();

  return (
    <div className={combinedClassName} role="alert">
      <div className="flex-shrink-0">{iconMap[type]}</div>
      <div className="flex-1 text-sm">{children}</div>
      {onClose && (
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          className="flex-shrink-0 ml-auto hover:opacity-70 transition-opacity border-0 p-1"
          aria-label="Close alert"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </Button>
      )}
    </div>
  );
};

export default Alert;
